/**
 * @data-dashboard/shared — CAPITAL-RETURN math shared by the API and the web app
 * (founder 2026-07-13). TWO pure derivations live here so there is exactly ONE
 * copy of each, testable by the API suite (web has no test runner) and imported
 * by the flagship "Capital return velocity" card, the "Your profit model"
 * equation strip, and the "Capital return multiple" planning card:
 *
 *  1. pctToFullBreakEven — the "% of the way to full break-even" the flagship
 *     shows when this window's customers haven't crossed yet. The equation strip
 *     REUSES this exact value (one shared derivation, never two — founder: two
 *     surfaces must never compute two different %s).
 *
 *  2. computeAnnualRoic — the founder's capital-output estimate from his book:
 *     each year the working
 *     capital redeploys `turnsPerYear` times (12 ÷ CRV months) and each turn funds
 *     a cohort that generates its full first-year LTV. At full recycling and flat
 *     CAC, the multiple = turns × (LTV ÷ cost). Two dials refine it: how much
 *     recovered capital keeps
 *     working) and a CAC-increase rate (later cohorts pay more to acquire).
 *
 * Both are PURE (no DOM, no React, no node) so the same function runs in the
 * browser (live dial feedback) and in a Node test.
 */

/**
 * "% of the way to full break-even" = how much of the per-customer cost bar the
 * climbing margin line has covered by the window's last measured day
 * (cumulative CM ÷ the full bar). ONE definition for every payback surface:
 *  - the flagship cash curve (bar = acquisition + overhead; inventory is a
 *    separate time stage because product cost is already inside CM),
 *  - the profit-model equation strip (bar = acquisition + overhead — the profit
 *    basis, since the waterfall is accrual).
 * Returns a fraction in [0, 1], or null when the bar isn't a positive number
 * (nothing to be a % of — unknown, never a fake 0%).
 */
export function pctToFullBreakEven(cumCmPerCustomer: number, costBar: number): number | null {
  if (!Number.isFinite(costBar) || costBar <= 0) return null;
  if (!Number.isFinite(cumCmPerCustomer)) return null;
  return Math.max(0, Math.min(1, cumCmPerCustomer / costBar));
}

export interface CumulativeValuePoint {
  day: number;
  cumulativeValue: number;
}

/** The whole customer day when a cumulative curve first crosses a dollar target.
 *  Shared by the server headline and the browser chart so both mark the same day. */
export function interpolatedCrossingDay(curve: ReadonlyArray<CumulativeValuePoint>, target: number): number | null {
  if (!Number.isFinite(target)) return null;
  if (target <= 0) return 0;
  for (let i = 0; i < curve.length; i++) {
    const point = curve[i]!;
    if (point.cumulativeValue < target) continue;
    if (i === 0) return Math.max(0, Math.round(point.day));
    const prior = curve[i - 1]!;
    const span = point.cumulativeValue - prior.cumulativeValue;
    if (span <= 0) return Math.max(0, Math.round(point.day));
    const fraction = (target - prior.cumulativeValue) / span;
    return Math.max(0, Math.round(prior.day + fraction * (point.day - prior.day)));
  }
  return null;
}

/** Cash-only Capital Return Velocity in days. Inventory is time before the sale,
 *  payback is the CM crossing of acquisition plus overhead, and settlement is the
 *  payout delay. Supplier terms can make inventory days negative when the customer
 *  pays before the supplier is due; that real working-capital head start shortens
 *  the total cycle. The final cycle is floored at zero because cash cannot return
 *  before the sale that created it. null means a required stage is not measurable. */
export function cashReturnDays(input: {
  inventoryDays: number | null;
  paybackDays: number | null;
  settlementDays: number | null;
}): number | null {
  const { inventoryDays, paybackDays } = input;
  const settlementDays = input.settlementDays ?? 0;
  if (inventoryDays === null || paybackDays === null) return null;
  if (![inventoryDays, paybackDays, settlementDays].every(Number.isFinite)) return null;
  if (paybackDays < 0 || settlementDays < 0) return null;
  return Math.max(0, inventoryDays + paybackDays + settlementDays);
}

/** How many times a year the same dollar of capital can turn over = 12 ÷ the
 *  Capital-Return-Velocity months. Clamped to a sane ceiling so a very short
 *  cycle can't project an absurd number of turns. null when CRV isn't live. */
export function cyclesPerYearFromCrvMonths(crvMonths: number | null): number | null {
  if (crvMonths === null || !Number.isFinite(crvMonths) || crvMonths <= 0) return null;
  // 24 turns/year (twice-monthly redeployment) is already an extreme ceiling for
  // a physical-goods business — cap there so a sub-month CRV can't runaway-compound.
  return Math.min(24, 12 / crvMonths);
}

export interface RoicInput {
  /** 1-year LTV per customer — REVENUE for the Gross view, contribution margin for
   *  the CM view (the card toggles which it passes in). */
  ltvPerCustomer: number | null;
  /** Acquisition cost per new customer — the part that rises as you scale volume. */
  cacPerCustomer: number | null;
  /** Overhead share per customer — fixed as you scale (the CAC-increase never touches it). */
  overheadPerCustomer: number | null;
  /** How many times the capital redeploys in a year = 12 ÷ CRV months. */
  turnsPerYear: number | null;
  /** Dial (0–100): share of the recovered ORIGINAL capital that funds the next
   *  turn. 100 = the whole starting dollar keeps working. 0 = one turn only.
   *  This does not compound profits. */
  reinvestmentRatePct: number;
  /** Dial: CAC rises this many % for every 10% more customers acquired (diminishing
   *  returns). 0 = CAC flat. A rough planning assumption — the card discloses it. */
  cacIncreasePer10PctVolume: number;
}

/**
 * Annual capital-return estimate from the founder's book model. Each year the working
 * capital redeploys `turnsPerYear` times (= 12 ÷ CRV months); each redeployment funds
 * a cohort that generates its full first-year value. The result is total first-year
 * Revenue or CM generated for each $1 of starting capital. It is not accounting ROIC.
 *
 * At 100% capital recycling and flat CAC: X = turnsPerYear × (LTV ÷ (CAC + overhead)).
 * Example: 4 turns × ($300 ÷ $125) = $9.60 generated for each starting $1.
 *
 * Two refinements the card has room for that a book keeps simple:
 *   - CAC rises with cumulative volume (diminishing returns): the k-th cohort pays
 *     CAC × k^e, where e = ln(1 + x) ÷ ln(1.1) from the "+x% per +10% customers" dial.
 *   - reinvestment: a share of each cohort's profit is plowed back, growing the pool.
 *
 * RUN-RATE by design (the card says so in its note): it counts each cohort's FULL
 * first-year LTV, including a cohort funded late in the year whose year runs into the
 * next — the earning POWER of the capital at this velocity, not literal in-year cash.
 * Returns null until the inputs are live.
 */
export function computeAnnualRoic(input: RoicInput): number | null {
  const ltv = input.ltvPerCustomer;
  const cac0 = input.cacPerCustomer;
  const oh = input.overheadPerCustomer;
  const N = input.turnsPerYear;
  if (ltv === null || cac0 === null || oh === null || N === null) return null;
  if (![ltv, cac0, oh, N].every((v) => Number.isFinite(v)) || cac0 + oh <= 0 || N <= 0 || ltv < 0) return null;
  const f = Math.max(0, Math.min(1, input.reinvestmentRatePct / 100));
  // "CAC +x% for every +10% customers" → power-law elasticity on cumulative volume.
  const x = Math.max(0, input.cacIncreasePer10PctVolume) / 100;
  const elasticity = x > 0 ? Math.log(1 + x) / Math.log(1.1) : 0;

  const turns = Math.min(24, N); // sane ceiling so a sub-month CRV can't runaway
  const whole = Math.floor(turns);
  const partial = turns - whole;
  let capital = 1; // per $1 of starting capital that keeps working
  let generated = 0; // total 1-year LTV generated across the year's cohorts
  // Each full turn: deploy the working capital, book the cohort's full 1-year LTV, then
  // redeploy the RECOVERED original capital (the cash comes back at CRV) at the
  // saved recycle rate. The rest leaves the model. f=1 keeps every dollar working (the book's
  // velocity); f<1 shrinks the pool each cycle, so the return falls, never explodes.
  // (The k-th cohort operates at ~k× the first cohort's cumulative volume, so its CAC
  // is cac0 × k^elasticity — diminishing returns.)
  for (let k = 0; k < whole; k++) {
    const cost = (elasticity > 0 ? cac0 * Math.pow(k + 1, elasticity) : cac0) + oh;
    generated += (capital / cost) * ltv;
    capital *= f; // recovered original capital retained for the next turn
  }
  if (partial > 1e-9) {
    const cost = (elasticity > 0 ? cac0 * Math.pow(whole + 1, elasticity) : cac0) + oh;
    generated += (capital / cost) * partial * ltv; // fractional final turn — no further redeploy
  }
  return generated;
}

/** The founder-plain headline: every $1 of starting capital generates $X.XX of
 * first-year value across the annual turns. Null when the estimate is unavailable. */
export function roicComesBackMultiple(input: RoicInput): number | null {
  return computeAnnualRoic(input);
}
